import { supabase } from '../../lib/supabase';
import { ProfileViewsData, ProfileView, ViewsAnalytics, ProfileBoost } from '../../types/dashboard/ProfileViews';
import { config } from '../../config/env';

/**
 * Profile view analytics.
 *
 * Reads from the activity signal log (profile_open / profile_dwell), not the
 * old profile_views table — nothing ever wrote that table, so this dashboard
 * was reporting on legacy rows only. See migration 049, which added the two
 * SECURITY DEFINER functions used below.
 *
 * Those functions exist because "who viewed me" needs rows where the caller is
 * the TARGET, and the log's RLS deliberately only grants access to rows where
 * you are the actor. Widening that policy would also expose who passed on you,
 * blocked you or reported you; the functions expose view signals and nothing
 * else.
 */
// Short-lived cache of computed chart data. Unrelated to profile_views — it
// stores the rendered analytics blob, not raw views.
const ANALYTICS_CACHE_TABLE = 'views_analytics_cache';

class ProfileViewsService {
  private readonly BOOSTS_TABLE = 'profile_boosts';

  /**
   * Get profile views data
   */
  async getProfileViews(userId: string): Promise<ProfileViewsData> {
    try {
      const now = new Date();

      // Counts are computed server-side and deduplicated to one view per
      // viewer per day, matching what profile_views used to store.
      const { data: statsRow, error: statsError } = await supabase
        .rpc('get_profile_view_stats', { p_user_id: userId })
        .single();

      if (statsError) throw statsError;

      const stats = statsRow as {
        total_views: number;
        views_today: number;
        views_yesterday: number;
        views_this_week: number;
        views_this_month: number;
        unique_viewers: number;
      } | null;

      // Get active boost
      const { data: activeBoost, error: boostError } = await supabase
        .from(this.BOOSTS_TABLE)
        .select('*')
        .eq('user_id', userId)
        .eq('is_active', true)
        .gt('expires_at', now.toISOString())
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (boostError && boostError.code !== 'PGRST116') {
        console.error('Error fetching active boost:', boostError);
      }

      return {
        totalViews: stats?.total_views ?? 0,
        viewsToday: stats?.views_today ?? 0,
        viewsYesterday: stats?.views_yesterday ?? 0,
        viewsThisWeek: stats?.views_this_week ?? 0,
        viewsThisMonth: stats?.views_this_month ?? 0,
        // Now a genuine distinct count — the old query passed a column to
        // `count: 'exact'`, which counts rows, so this reported total views
        // rather than unique viewers.
        uniqueViewers: stats?.unique_viewers ?? 0,
        lastUpdated: new Date()
      };
    } catch (error) {
      console.error('Failed to get profile views:', error);
      throw error;
    }
  }

  /**
   * Get recent viewers
   */
  async getRecentViewers(userId: string, limit: number = 10): Promise<ProfileView[]> {
    try {
      // One row per viewer with their view count and total dwell, rather than
      // one row per view — the signal log records every open, so an unrolled
      // list would repeat the same person down the page.
      const { data, error } = await supabase
        .rpc('get_profile_viewers', { p_user_id: userId, p_limit: limit });

      if (error) {
        console.error('Error fetching recent viewers:', error);
        throw error;
      }

      return (data ?? []).map((row: any): ProfileView => ({
        id: `${row.viewer_id}:${row.last_viewed_at}`,
        viewerId: row.viewer_id,
        viewerName: `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim() || 'Anonymous',
        viewerPhoto: row.main_photo_url ?? undefined,
        viewedAt: new Date(row.last_viewed_at),
      }));
    } catch (error) {
      console.error('Failed to get recent viewers:', error);
      throw error;
    }
  }

  /**
   * Get views analytics
   */
  async getViewsAnalytics(
    userId: string,
    timeframe: 'day' | 'week' | 'month'
  ): Promise<ViewsAnalytics> {
    try {
      // Check cache first
      const { data: cachedData, error: cacheError } = await supabase
        .from(ANALYTICS_CACHE_TABLE)
        .select('*')
        .eq('user_id', userId)
        .eq('timeframe', timeframe)
        .gt('expires_at', new Date().toISOString())
        .single();

      if (!cacheError && cachedData) {
        return cachedData.analytics_data;
      }

      // Generate fresh analytics
      const analytics = await this.generateAnalytics(userId, timeframe);

      // Cache the results
      await this.cacheAnalytics(userId, timeframe, analytics);

      return analytics;
    } catch (error) {
      console.error('Failed to get views analytics:', error);
      throw error;
    }
  }

  /**
   * Boost profile
   */
  async boostProfile(userId: string, duration: number): Promise<void> {
    try {
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + duration);

      const { error } = await supabase
        .from(this.BOOSTS_TABLE)
        .insert({
          user_id: userId,
          boost_type: 'standard',
          boost_duration: duration,
          boost_multiplier: 3.0,
          expires_at: expiresAt.toISOString()
        });

      if (error) {
        console.error('Error boosting profile:', error);
        throw error;
      }
    } catch (error) {
      console.error('Failed to boost profile:', error);
      throw error;
    }
  }

  /**
   * Removed: views are recorded by the activity signal log.
   *
   * This method wrote to profile_views and had no callers anywhere in the app,
   * which is why the dashboard had nothing recent to show. Opening a profile
   * now emits profile_open (and profile_dwell on close) from
   * useProfilePeek/ProfilePeekDrawer, which is where every feed opens a
   * profile. Nothing needs to be called here.
   */

  /**
   * Generate analytics data
   */
  private async generateAnalytics(
    userId: string,
    timeframe: 'day' | 'week' | 'month'
  ): Promise<ViewsAnalytics> {
    const now = new Date();
    let startDate: Date;
    let viewsData: any[] = [];

    // Set start date based on timeframe
    switch (timeframe) {
      case 'day':
        startDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'week':
        startDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        startDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
    }

    // Per-view events from the signal log, normalised to the field names the
    // charting below already uses.
    const { data: events, error } = await supabase
      .rpc('get_profile_view_events', {
        p_user_id: userId,
        p_since: startDate.toISOString(),
      });

    if (error) {
      console.error('Error generating analytics:', error);
      throw error;
    }

    interface ViewRow {
      viewer_id: string;
      created_at: string;
      view_duration: number;
      view_source: string;
    }

    const data: ViewRow[] = (events ?? []).map((e: any) => ({
      viewer_id: e.viewer_id,
      created_at: e.occurred_at,
      view_duration: Math.round((e.duration_ms ?? 0) / 1000),
      view_source: e.surface ?? 'discovery',
    }));

    // Process data based on timeframe
    if (timeframe === 'day') {
      // Group by hour
      const hourlyData = new Array(24).fill(0).map((_, hour) => ({
        time: hour,
        views: 0,
        label: `${hour}:00`
      }));

      data?.forEach(view => {
        const hour = new Date(view.created_at).getHours();
        hourlyData[hour].views++;
      });

      viewsData = hourlyData;
    } else if (timeframe === 'week') {
      // Group by day
      const dailyData = new Array(7).fill(0).map((_, day) => {
        const date = new Date(now.getTime() - (6 - day) * 24 * 60 * 60 * 1000);
        return {
          time: day,
          views: 0,
          label: date.toLocaleDateString('en-US', { weekday: 'short' })
        };
      });

      data?.forEach(view => {
        const viewDate = new Date(view.created_at);
        const dayIndex = Math.floor((viewDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
        if (dayIndex >= 0 && dayIndex < 7) {
          dailyData[dayIndex].views++;
        }
      });

      viewsData = dailyData;
    } else {
      // Group by day for month
      const dailyData = new Array(30).fill(0).map((_, day) => {
        const date = new Date(now.getTime() - (29 - day) * 24 * 60 * 60 * 1000);
        return {
          time: day,
          views: 0,
          label: date.getDate().toString()
        };
      });

      data?.forEach(view => {
        const viewDate = new Date(view.created_at);
        const dayIndex = Math.floor((viewDate.getTime() - startDate.getTime()) / (24 * 60 * 60 * 1000));
        if (dayIndex >= 0 && dayIndex < 30) {
          dailyData[dayIndex].views++;
        }
      });

      viewsData = dailyData;
    }

    // Calculate peak hours and days
    const peakHours = this.calculatePeakHours(data || []);
    const peakDays = this.calculatePeakDays(data || []);

    // Get unique viewers count
    const uniqueViewerIds = new Set(data?.map(view => view.viewer_id) || []);
    const uniqueViewers = uniqueViewerIds.size;

    // Convert viewsData to viewsByDay format
    const viewsByDay = viewsData.map(item => ({
      date: item.label || '',
      views: item.views
    }));

    // Determine peak viewing time
    const peakHour = peakHours.length > 0 ? peakHours[0] : 19;
    const peakViewingTime = `${peakHour}:00-${peakHour + 2}:00`;

    return {
      timeframe,
      totalViews: data?.length || 0,
      uniqueViewers,
      viewsByDay,
      peakViewingTime,
      demographics: {
        ageGroups: {},
        locations: {}
      }
    };
  }

  /**
   * Cache analytics data
   */
  private async cacheAnalytics(
    userId: string,
    timeframe: string,
    analytics: ViewsAnalytics
  ): Promise<void> {
    try {
      const expiresAt = new Date();
      expiresAt.setHours(expiresAt.getHours() + 1); // Cache for 1 hour

      const { error } = await supabase
        .from(ANALYTICS_CACHE_TABLE)
        .upsert({
          user_id: userId,
          timeframe,
          analytics_data: analytics,
          expires_at: expiresAt.toISOString()
        });

      if (error) {
        console.error('Error caching analytics:', error);
      }
    } catch (error) {
      console.error('Failed to cache analytics:', error);
    }
  }

  /**
   * Calculate peak viewing hours
   */
  private calculatePeakHours(views: any[]): number[] {
    const hourlyCounts = new Array(24).fill(0);
    
    views.forEach(view => {
      const hour = new Date(view.created_at).getHours();
      hourlyCounts[hour]++;
    });

    const maxCount = Math.max(...hourlyCounts);
    return hourlyCounts
      .map((count, hour) => ({ hour, count }))
      .filter(({ count }) => count === maxCount)
      .map(({ hour }) => hour);
  }

  /**
   * Calculate peak viewing days
   */
  private calculatePeakDays(views: any[]): string[] {
    const dailyCounts = new Array(7).fill(0);
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    
    views.forEach(view => {
      const day = new Date(view.created_at).getDay();
      dailyCounts[day]++;
    });

    const maxCount = Math.max(...dailyCounts);
    return dailyCounts
      .map((count, day) => ({ day, count }))
      .filter(({ count }) => count === maxCount)
      .map(({ day }) => dayNames[day]);
  }

  /**
   * Generate insights
   */
  private generateInsights(views: any[], viewsData: any[]): string[] {
    const insights: string[] = [];
    
    if (views.length === 0) {
      insights.push('No profile views yet. Complete your profile to get more visibility!');
      return insights;
    }

    // Peak time insight
    const maxViews = Math.max(...viewsData.map(d => d.views));
    const peakTime = viewsData.find(d => d.views === maxViews);
    if (peakTime) {
      insights.push(`Your profile gets most views at ${peakTime.label}`);
    }

    // Average view duration
    const avgDuration = views.reduce((sum, view) => sum + (view.view_duration || 0), 0) / views.length;
    if (avgDuration > 30) {
      insights.push('People spend a good amount of time viewing your profile');
    } else {
      insights.push('Consider improving your profile to increase engagement time');
    }

    // View source insights
    const sourceCounts = views.reduce((counts, view) => {
      counts[view.view_source] = (counts[view.view_source] || 0) + 1;
      return counts;
    }, {} as Record<string, number>);

    const topSource = Object.entries(sourceCounts).sort(([,a], [,b]) => (b as number) - (a as number))[0];
    if (topSource) {
      insights.push(`Most views come from ${topSource[0]} discovery`);
    }

    // General tips
    insights.push('Keep your profile updated to maintain interest');
    insights.push('Add more photos to increase profile attractiveness');

    return insights;
  }

  /**
   * Transform database record to ProfileView
   */
  private transformProfileView = (record: any): ProfileView => {
    return {
      id: record.id,
      viewerId: record.viewer_id,
      viewerName: record.users ? 
        `${record.users.first_name || ''} ${record.users.last_name || ''}`.trim() || record.users.email :
        'Anonymous',
      viewerPhoto: record.users?.photo_url,
      viewedAt: new Date(record.created_at),
      isPremium: record.users?.is_premium || false
    };
  };
}

export const profileViewsService = new ProfileViewsService();
